<template>
  <v-container class="fill-height" style="opacity: 0.94" fluid>
    <v-sheet elevation="1" rounded width="100%" class="mt-16 mb-8">
      <v-row justify="space-between" class="pa-2">
        <v-col cols="12" md="6">
          <v-text-field
            v-model="searchQuery"
            :label="$t('Search')"
            prepend-icon="mdi-magnify"
            placeholder="e.g. 1234"
            clearable
            @keyup.enter="searchOrders(searchQuery)"
            @click:clear="searchOrders('')"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="selectedStore"
            :items="stores"
            @change="changeStore"
            item-text="domain"
            value="id"
            :label="$t('Select Store')"
            prepend-icon="mdi-city-variant-outline"
            return-object
          ></v-select>
        </v-col>
        <v-col align="right">
          <v-btn-toggle v-if="selected.length === 0">
            <v-menu :close-on-content-click="false" offset-y :transition="false">
              <template #activator="{ on, attrs }">
                <v-btn large value="center" v-bind="attrs" v-on="on">
                  <v-icon>mdi-view-column-outline</v-icon>
                  <span class="hidden-sm-and-down">{{ $t('Columns') }}</span>
                </v-btn>
              </template>

              <v-card min-width="205px">
                <v-list>
                  <template v-for="item in headers">
                    <v-list-item
                      class="text-left"
                      link
                      v-if="(item.admin && isAdmin) || !item.admin"
                      :key="item.value"
                    >
                      <v-list-item-action>
                        <v-checkbox v-model="item.active" :disabled="item.fixed" />
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>{{ $t(item.text) }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-list>
              </v-card>
            </v-menu>

            <v-menu :close-on-content-click="false" offset-y :transition="false">
              <template #activator="{ on, attrs }">
                <v-btn large value="right" v-bind="attrs" v-on="on">
                  <v-icon>mdi-swap-vertical</v-icon>
                  <span class="hidden-sm-and-down">{{ $t('Sort') }}</span>
                </v-btn>
              </template>

              <v-card min-width="205px">
                <v-card-text>
                  <v-subheader>Group By</v-subheader>
                  <v-radio-group v-model="groupedAs" column>
                    <v-radio :label="$t('None')" :value="null"></v-radio>
                    <v-radio :label="$t('Variant')" value="variants[0]"></v-radio>
                    <v-radio :label="$t('Date')" value="calendarDate"></v-radio>
                    <v-radio :label="$t('Colors')" value="colors[0]"></v-radio>
                  </v-radio-group>
                </v-card-text>
              </v-card>
            </v-menu>
          </v-btn-toggle>
          <v-btn-toggle v-else>
            <v-menu>
              <template v-slot:activator="{ on, attrs }">
                <v-btn :disabled="loading" large value="right" v-bind="attrs" v-on="on">
                  <v-icon>mdi-cloud-download-outline</v-icon>
                  {{ $t('Print') }}
                </v-btn>
              </template>
              <v-list>
                <v-list-item @click="downloadZip('print')"> Separate PDFs </v-list-item>
                <v-list-item @click="downloadZip('print', true)"> Combined PDFs </v-list-item>
              </v-list>
            </v-menu>

            <v-menu>
              <template v-slot:activator="{ on, attrs }">
                <v-btn :disabled="loading" large value="center" v-bind="attrs" v-on="on">
                  <v-icon>mdi-cloud-download-outline</v-icon>
                  {{ $t('Digital') }}
                </v-btn>
              </template>
              <v-list>
                <v-list-item @click="downloadZip('digital')"> Separate PDFs </v-list-item>
                <v-list-item @click="downloadZip('digital', true)"> Combined PDFs </v-list-item>
              </v-list>
            </v-menu>

            <v-menu>
              <template v-slot:activator="{ on, attrs }">
                <v-btn :disabled="loading" large value="center" v-bind="attrs" v-on="on">
                  <v-icon>mdi-cloud-download-outline</v-icon>
                  {{ $t('Greeting Cards') }}
                </v-btn>
              </template>
              <v-list>
                <v-list-item @click="greetingCardZip(false, 'stars')"> STARS </v-list-item>
                <v-list-item @click="greetingCardZip(false, 'meteorites')">
                  METEORITES
                </v-list-item>
                <v-list-item @click="greetingCardZip(false, 'all')"> ALL </v-list-item>
              </v-list>
            </v-menu>
            <v-menu>
              <template v-slot:activator="{ on, attrs }">
                <v-btn :disabled="loading" large value="center" v-bind="attrs" v-on="on">
                  <v-icon>mdi-cloud-download-outline</v-icon>
                  {{ $t('Star Charts') }}
                </v-btn>
              </template>
              <v-list>
                <v-list-item @click="starChartZip(true)"> Separate PDFs </v-list-item>
                <v-list-item @click="starChartZip(false)"> Combined PDFs </v-list-item>
              </v-list>
            </v-menu>
          </v-btn-toggle>
        </v-col>
      </v-row>
      <v-data-table
        ref="dataTable"
        v-model="selected"
        :headers="activeHeaders"
        height="65vh"
        fixed-header
        :items="tableRows"
        item-key="id"
        :items-per-page="50"
        class="elevation-1"
        :loading="loading"
        show-select
        loading-text="Loading... Please wait"
        :group-by="groupedAs"
        hide-default-footer
      >
        <template v-slot:[`header.data-table-select`]="{ props, on }">
          <v-simple-checkbox
            :value="props.value || props.indeterminate"
            v-on="on"
            :indeterminate="props.indeterminate"
            color="secondary"
          />
        </template>
        <template v-slot:[`group.header`]="{ group, headers, toggle, isOpen }">
          <td :colspan="headers.length" class="text-left">
            <v-btn @click="toggle" x-small icon :ref="group">
              <v-icon v-if="isOpen">mdi-plus</v-icon>
              <v-icon v-else>mdi-minus</v-icon>
            </v-btn>
            <span class="mx-5 font-weight-bold">{{ group }}</span>
          </td>
        </template>
        <template v-slot:[`item.orderNumber`]="{ item }">
          <td class="fixed-col">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <!-- v-for over a single-element array = local `badges` binding for this row -->
                <div v-for="(badges, group) in [orderBadges(item)]" :key="group">
                  <v-badge
                    light
                    bordered
                    style="font-weight: 700"
                    v-if="badges.length"
                    :color="badges[0].color"
                    :content="badges[0].content"
                  >
                    <v-badge
                      :value="badges.length > 1"
                      :color="badges[1] ? badges[1].color : ''"
                      :content="badges[1] ? badges[1].content : ''"
                      offset-x="-1"
                      offset-y="7"
                    >
                      <v-badge
                        :value="badges.length > 2"
                        :color="badges[2] ? badges[2].color : ''"
                        :content="badges[2] ? badges[2].content : ''"
                        offset-x="-1"
                        offset-y="24"
                      >
                        <a
                          class="text-white text-decoration-none font-weight-bold"
                          style="color: lightgrey"
                          target="_blank"
                          :href="`${selectedStore.shopUrl}/orders/${item.id}`"
                          v-bind="attrs"
                          v-on="on"
                        >
                          # {{ item.orderNumber }}
                        </a>
                      </v-badge>
                    </v-badge>
                  </v-badge>
                  <a
                    v-else
                    class="text-white text-decoration-none font-weight-bold"
                    style="color: lightgrey"
                    target="_blank"
                    :href="`${selectedStore.shopUrl}/orders/${item.id}`"
                    v-bind="attrs"
                    v-on="on"
                  >
                    # {{ item.orderNumber }}
                  </a>
                </div>
              </template>
              <span>
                {{ $t('Open in shopify') }}
                <v-icon small>mdi-open-in-new</v-icon>
              </span>
            </v-tooltip>
          </td>
        </template>

        <template v-slot:[`item.createdAt`]="{ item }">
          {{ item.createdAt | moment }}
        </template>

        <template v-slot:[`item.customer`]="{ item }">
          <v-menu :close-on-content-click="false" max-width="min-content" offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-hover v-slot="{ hover }">
                <v-btn plain v-bind="attrs" v-on="on" class="text-none">
                  {{ `${item.customer.firstName} ${item.customer.lastName}` }}
                  <v-icon :style="`opacity:${hover ? 1 : 0}`" right> mdi-chevron-down </v-icon>
                </v-btn>
              </v-hover>
            </template>
            <customer-card
              :title="`${item.customer.firstName} ${item.customer.lastName}`"
              :subtitle="`${item.customer.city} ${item.customer.provinceCode}, ${item.customer.country}`"
              :description="item.customer.email"
              :third="item.customer.ordersCount"
              :action="`${selectedStore.shopUrl}/customers/${item.customer.id}`"
            ></customer-card>
          </v-menu>
        </template>

        <template v-slot:[`item.totalPrice`]="{ item }"> ${{ item.totalPrice }} </template>

        <template v-slot:[`item.financialStatus`]="{ item }">
          <v-chip
            class="ma-2 text-capitalize"
            small
            :color="getFinanceStatus(item.financialStatus).color || 'secondary'"
          >
            <v-icon left small>
              {{ getFinanceStatus(item.financialStatus).icon }}
            </v-icon>
            {{ getFinanceStatus(item.financialStatus).label }}
          </v-chip>
        </template>

        <template v-slot:[`item.fulfillmentStatus`]="{ item }">
          <email-dialog
            :title="$t('Order Fulfillment')"
            :data="item"
            :store="selectedStore.id"
            @preview="previewCert"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                class="ma-2"
                small
                :color="`${getFulfillStatus(item.fulfillmentStatus)[2]}`"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon left small>
                  {{ getFulfillStatus(item.fulfillmentStatus)[1] }}
                </v-icon>
                {{ getFulfillStatus(item.fulfillmentStatus)[0] }}
              </v-chip>
            </template>
          </email-dialog>
        </template>

        <template v-slot:[`item.orderItems`]="{ item }">
          <v-menu :close-on-content-click="false" :max-width="300" max-height="50vh" offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-hover v-slot="{ hover }">
                <v-btn plain v-bind="attrs" v-on="on" class="text-none">
                  {{ `${item.lineItems.length} ${$tc('item', item.lineItems.length)}` }}
                  <v-icon :style="`opacity:${hover ? 1 : 0}`" right> mdi-chevron-down </v-icon>
                </v-btn>
              </v-hover>
            </template>
            <v-list align="left">
              <v-list-item two-line v-for="(orderItem, index) in item.lineItems" :key="index">
                <v-badge
                  offset-x="30"
                  offset-y="30"
                  overlap
                  bordered
                  color="secondary"
                  :content="orderItem.quantity"
                >
                  <v-list-item-avatar tile height="50" width="40">
                    <v-img :src="orderItem.variantImage"></v-img>
                  </v-list-item-avatar>
                </v-badge>
                <v-list-item-content>
                  <v-list-item-title class="text-wrap">
                    <a target="_blank" :href="`${selectedStore.shopUrl}/products/${orderItem.id}`">
                      {{ orderItem.title }}
                    </a>
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-wrap">
                    {{ orderItem.subtitle }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <template v-slot:[`item.tags`]="{ item }">
          <template v-for="tag in item.tags.split(', ')">
            <v-chip v-if="tag" class="ma-2 text-capitalize" small :key="tag">
              {{ tag }}
            </v-chip>
          </template>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-row style="width: 256px">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on"><v-icon>mdi-chevron-down</v-icon></v-btn>
              </template>
              <v-list class="text-start">
                <v-list-item @click="generateStarChart(item)">
                  <v-list-item-icon>
                    <v-icon :color="item.print ? 'lime darken-1' : 'secondary'">
                      mdi-chart-timeline-variant-shimmer
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ $t('Generate Star Chart') }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="generateCardStarChart(item)">
                  <v-list-item-icon>
                    <v-icon :color="item.print ? 'lime darken-1' : 'secondary'">
                      mdi-card-account-details-star-outline
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ $t('Card Sized Star Chart') }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="generateZodiacNecklace(item)">
                  <v-list-item-icon>
                    <v-icon :color="item.print ? 'lime darken-1' : 'secondary'">
                      mdi-necklace
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ $t('Zodiac Necklace') }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <email-dialog
              :title="$t('Order Fulfillment')"
              :data="item"
              :store="selectedStore.id"
              @preview="previewCert"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="orange darken-1"
                  icon
                  v-bind="attrs"
                  v-on="on"
                  :disabled="item.stars.length === 0"
                >
                  <v-icon> mdi-email-fast-outline </v-icon>
                </v-btn>
              </template>
            </email-dialog>

            <template v-if="isHoroscopeProduct(item)">
              <!-- Per book: a plus book has print, a basic one only digital — greyed like the cert buttons. -->
              <horoscope-book-dialog
                :order="item"
                :store="selectedStore"
                track="print"
                @download-zip="generateHoroscopeAssets(item.id, item.orderNumber, 'print')"
              >
                <template v-slot:activator="{ on: onDialog, attrs }">
                  <v-tooltip bottom>
                    <template #activator="{ on: onTooltip }">
                      <v-btn
                        icon
                        :disabled="!horoscopeHas(item, 'print')"
                        :color="'error'"
                        v-bind="attrs"
                        v-on="{ ...onTooltip, ...onDialog }"
                      >
                        <v-icon>mdi-printer-wireless</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('Horoscope Book') }}</span>
                  </v-tooltip>
                </template>
              </horoscope-book-dialog>
              <horoscope-book-dialog
                :order="item"
                :store="selectedStore"
                track="digital"
                @download-zip="generateHoroscopeAssets(item.id, item.orderNumber, 'digital')"
              >
                <template v-slot:activator="{ on: onDialog, attrs }">
                  <v-tooltip bottom>
                    <template #activator="{ on: onTooltip }">
                      <v-btn
                        icon
                        :disabled="!horoscopeHas(item, 'digital')"
                        :color="'error'"
                        v-bind="attrs"
                        v-on="{ ...onTooltip, ...onDialog }"
                      >
                        <v-icon>mdi-file-pdf-box</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('Digital Horoscope Book') }}</span>
                  </v-tooltip>
                </template>
              </horoscope-book-dialog>
            </template>

            <template
              v-if="
                !isHoroscopeProduct(item) && isShootingStarOrder(item) && !isMixedCertOrder(item)
              "
            >
              <v-tooltip bottom>
                <template #activator="{ on: onTooltip }">
                  <v-btn
                    icon
                    :color="'error'"
                    v-on="{ ...onTooltip }"
                    @click="generateShootingStarCert(item.id, 'print')"
                  >
                    <v-icon>mdi-printer-wireless</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('Generate Print Version') }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template #activator="{ on: onTooltip }">
                  <v-btn
                    icon
                    :color="'error'"
                    v-on="{ ...onTooltip }"
                    @click="generateShootingStarCert(item.id, 'digital')"
                  >
                    <v-icon>mdi-file-pdf-box</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('Generate Digital Version') }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-btn
                    v-on="on"
                    icon
                    @click="generateGreetingCard(item)"
                    :disabled="!shootingStarGreetingMessages(item).length"
                  >
                    <v-icon :color="item.print ? 'lime darken-1' : 'secondary'">
                      mdi-message-bookmark-outline
                    </v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('Generate Greeting Card') }}</span>
              </v-tooltip>
            </template>

            <!--
              Mixed order (star + meteorite): keep the row at two cert buttons and move the
              choice into the menu — Print/Digital > product > colour (stars only).
            -->
            <template v-if="!isHoroscopeProduct(item) && isMixedCertOrder(item)">
              <v-menu
                v-for="version in certVersions"
                :key="version.key"
                offset-y
                :close-on-content-click="false"
                :value="openCertMenu === `${item.id}:${version.key}`"
                @input="openCertMenu = $event ? `${item.id}:${version.key}` : null"
              >
                <template #activator="{ on: onMenu }">
                  <v-tooltip bottom>
                    <template #activator="{ on: onTooltip }">
                      <v-btn
                        icon
                        :color="item[version.key] ? version.activeColor : 'secondary'"
                        v-on="{ ...onMenu, ...onTooltip }"
                      >
                        <v-badge
                          bordered
                          left
                          light
                          overlap
                          style="font-weight: 700"
                          :value="version.key === 'print' && !!item.copies"
                          color="blue darken-2"
                          :content="`+${item.copies}`"
                        >
                          <v-icon>{{ version.icon }}</v-icon>
                        </v-badge>
                      </v-btn>
                    </template>
                    <span>{{ $t(version.label) }}</span>
                  </v-tooltip>
                </template>
                <v-list class="text-start cert-menu-list" dense>
                  <v-list-group :disabled="item.stars.length === 0">
                    <template #activator>
                      <v-list-item-content>
                        <v-list-item-title>{{ $t('Star Certificate') }}</v-list-item-title>
                      </v-list-item-content>
                    </template>
                    <v-list-item
                      v-for="(color, index) in selectedStore.colors"
                      :key="index"
                      link
                      @click="runCertAction(() => generateCert(item, color, version.key))"
                    >
                      <v-img :src="getImageSrc(color, item)" width="50px"></v-img>
                      <v-list-item-title class="ml-2">{{ color.title }}</v-list-item-title>
                    </v-list-item>
                  </v-list-group>
                  <v-list-item
                    link
                    @click="runCertAction(() => generateShootingStarCert(item.id, version.key))"
                  >
                    <v-list-item-title>{{ $t('Meteorite Certificate') }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-btn
                    v-on="on"
                    icon
                    @click="generateGreetingCard(item)"
                    :disabled="!shootingStarGreetingMessages(item).length && !item.greeting"
                  >
                    <v-icon :color="item.print ? 'lime darken-1' : 'secondary'">
                      mdi-message-bookmark-outline
                    </v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('Generate Greeting Card') }}</span>
              </v-tooltip>
            </template>

            <template v-if="!isHoroscopeProduct(item) && !isShootingStarOrder(item)">
              <v-menu offset-y>
                <template #activator="{ on: onMenu }">
                  <v-tooltip bottom>
                    <template #activator="{ on: onTooltip }">
                      <v-btn
                        icon
                        :color="item.print ? 'error' : 'secondary'"
                        v-on="{ ...onMenu, ...onTooltip }"
                        :disabled="item.stars.length === 0"
                      >
                        <v-badge
                          bordered
                          left
                          light
                          overlap
                          style="font-weight: 700"
                          :value="item.copies ? true : false"
                          color="blue darken-2"
                          :content="`+${item.copies}`"
                        >
                          <v-icon>mdi-printer-wireless</v-icon>
                        </v-badge>
                      </v-btn>
                    </template>
                    <span>{{ $t('Generate Print Version') }}</span>
                  </v-tooltip>
                </template>
                <v-list>
                  <v-list-item
                    link
                    v-for="(color, index) in selectedStore.colors"
                    :key="index"
                    @click="generateCert(item, color, 'print')"
                    class="text-start"
                  >
                    <v-img :src="getImageSrc(color, item)" width="50px"></v-img>
                    <v-list-item-title class="ml-2">{{ color.title }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <v-menu offset-y>
                <template #activator="{ on: onMenu }">
                  <v-tooltip bottom>
                    <template #activator="{ on: onTooltip }">
                      <v-btn
                        icon
                        :color="item.digital ? 'pink' : 'secondary'"
                        v-on="{ ...onMenu, ...onTooltip }"
                        :disabled="item.stars.length === 0"
                      >
                        <v-icon>mdi-file-pdf-box</v-icon>
                      </v-btn>
                    </template>

                    <span>{{ $t('Generate Digital Version') }}</span>
                  </v-tooltip>
                </template>
                <v-list>
                  <v-list-item
                    link
                    v-for="(color, index) in selectedStore.colors"
                    :key="index"
                    @click="generateCert(item, color, 'digital')"
                    class="text-start"
                  >
                    <v-img :src="getImageSrc(color, item)" width="50px"></v-img>
                    <v-list-item-title class="ml-2">{{ color.title }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>

            <v-tooltip bottom v-if="!isShootingStarOrder(item)">
              <template #activator="{ on }">
                <v-btn
                  v-on="on"
                  icon
                  @click="generateGreetingCard(item)"
                  :disabled="!item.greeting"
                >
                  <v-icon :color="item.print ? 'lime darken-1' : 'secondary'">
                    mdi-message-bookmark-outline
                  </v-icon>
                </v-btn>
              </template>
              <span>{{ $t('Generate Greeting Card') }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  target="_blank"
                  :href="softwareUrl + getRegNum(item.stars)"
                  :disabled="item.stars.length === 0"
                >
                  <v-icon>mdi-search-web</v-icon>
                </v-btn>
              </template>
              <span>
                {{ $t('View in software') }}
                <v-icon small>mdi-open-in-new</v-icon>
              </span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="error"
                  v-bind="attrs"
                  v-on="on"
                  @click="renewRegistry(item)"
                  :disabled="item.stars.length === 0"
                >
                  <v-icon>mdi-reload-alert</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('Update Registry From Shopify') }}</span>
            </v-tooltip>
          </v-row>
        </template>

        <template v-slot:footer="{}">
          <v-divider></v-divider>
          <div class="my-2 pa-2">
            <v-btn icon @click="paginate('previous')" :disabled="currentPage <= 0">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn icon @click="paginate('next')">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-sheet>
  </v-container>
</template>

<script>
  import moment from 'moment'
  import utils from '@/assets/js/utils'
  import customerCard from '../components/CustomerCard'
  import emailDialog from '../components/EmailDialog'
  import horoscopeBookDialog from '../components/HoroscopeBookDialog'
  import { getOrdersShopify, renewRegistryShopify } from '../services/shopifyService'
  import {
    createCert,
    createGreetCard,
    createHoroscopeAssets,
    createStarInfoCard,
    createZodiacNecklace,
    createShootingStarCert,
  } from '../services/pdfGenerationService'
  import { getStoreById, getStoresProtected } from '../services/storeService'
  import { isUserAdmin } from '../services/authService'
  import JSZip from 'jszip'

  export default {
    data() {
      return {
        stores: [],
        isAdmin: false,
        softwareUrl: 'https://software.space-registry.org/registry/',
        selectedStore: null,
        headers: [
          {
            text: 'Order',
            value: 'orderNumber',
            active: true,
            width: '100px',
          },
          { text: 'Date', value: 'createdAt', width: '200px', active: true },
          { text: 'Customer', value: 'customer', active: true },
          { text: 'Total', value: 'totalPrice', active: false, admin: true },
          { text: 'Payment Status', value: 'financialStatus', active: true },
          {
            text: 'Fulfillment Status',
            value: 'fulfillmentStatus',
            active: true,
          },
          { text: 'Items', value: 'orderItems', active: true },
          { text: 'Delivery Method', value: 'delivery', active: false },
          { text: 'Tags', value: 'tags', active: false },
          { text: 'Actions', value: 'actions', active: true },
        ],
        filters: [
          { text: 'Show Fulfilled Orders', prop: 'f1' },
          { text: 'Show Unfulfilled Orders', prop: 'f1' },
          { text: 'Show Star Orders', prop: 'f1' },
          { text: 'Show Print Orders', prop: 'f1' },
          { text: 'Show Digital Orders', prop: 'f1' },
          { text: 'Show Paid Orders', prop: 'f1' },
        ],
        orders: [],
        tableRows: [],
        selected: [],
        groupedAs: null,
        searchQuery: '',
        loading: true,
        pages: { next: '', previous: '' },
        currentPage: 0,
        apiUrl: `${process.env.VUE_APP_YOUVENDO_API_SERVER}/`,
        certVersions: [
          {
            key: 'print',
            icon: 'mdi-printer-wireless',
            label: 'Generate Print Version',
            activeColor: 'error',
          },
          {
            key: 'digital',
            icon: 'mdi-file-pdf-box',
            label: 'Generate Digital Version',
            activeColor: 'pink',
          },
        ],
        // `${orderId}:${version}` of the open mixed-order cert menu, or null
        openCertMenu: null,
      }
    },
    methods: {
      isHoroscopeProduct: (item) => {
        return !!item.lineItems.find((lineItem) =>
          lineItem.title.toLowerCase().startsWith('personalisiertes')
        )
      },
      /** Whether any horoscope book on the order was bought with this delivery (`horoscope` from the listing). */
      horoscopeHas: (item, delivery) => {
        return item.lineItems.some((lineItem) => lineItem.horoscope && lineItem.horoscope[delivery])
      },
      fetchOrders: async function () {
        const { id: storeId } = this.$route.params
        if (!storeId) {
          this.changeStore(this.stores[0])
        }
        this.selectedStore = await getStoreById(storeId)

        const response = (
          await getOrdersShopify('current', this.selectedStore.id, this.searchQuery || undefined)
        )?.data
        this.orders = response?.orders || []
        this.pages = response?.pages
        this.tableRows = [...this.orders]
      },
      async paginate(value) {
        const table = this.$refs.dataTable
        const wrapper = table.$el.querySelector('div.v-data-table__wrapper')

        // Scroll to top of table
        this.$vuetify.goTo(table, { container: wrapper }) // to header of table

        this.loading = true

        const response = (await getOrdersShopify(this.pages[value], this.selectedStore.id))?.data
        this.orders = response?.orders || []
        this.pages = response?.pages
        this.tableRows = [...this.orders]

        this.loading = false
        this.currentPage += value === 'previous' ? -1 : 1
      },
      // Keeps ?search= in sync so the current search survives a reload / shared link
      syncSearchParam() {
        const search = this.searchQuery || undefined
        if (this.$route.query.search === search) return
        this.$router.replace({ query: { ...this.$route.query, search } }).catch(() => {})
      },
      searchOrders: async function (keywords) {
        this.searchQuery = keywords || ''
        this.syncSearchParam()

        this.loading = true
        this.currentPage = 0
        await this.fetchOrders()
        this.loading = false
      },
      shootingStarGreetingMessages(order) {
        return order.lineItems.map((li) => li.greetingText).filter(Boolean)
      },
      async generateGreetingCard(order) {
        const { stars: regKeys } = order
        try {
          this.loading = true
          const greetingMessages = this.shootingStarGreetingMessages(order)
          const { data } = await createGreetCard(
            regKeys,
            this.selectedStore.id,
            false,
            false,
            greetingMessages.length ? greetingMessages : null
          )

          if (data.length === 0) {
            alert('No greeting message found!')
          }

          // stars
          data.forEach((item) => {
            if (typeof item === 'string') {
              window.open(this.apiUrl + item, '_blank')
            } else {
              window.open(item.url, '_blank')
            }
          })
        } catch (error) {
          console.log(error)
        } finally {
          this.loading = false
        }
      },
      async greetingCardZip(zip, type) {
        try {
          this.loading = true
          const merge = true

          const regKeys = []
          const messages = []
          this.selected
            .filter((i) => i.variants.length)
            .forEach((selection) => {
              regKeys.push(...selection.stars)
            })
          this.selected
            .filter((i) => this.isShootingStarOrder(i))
            .forEach((selection) => {
              messages.push(...this.shootingStarGreetingMessages(selection))
            })

          let starResponse, shootingStarResponse
          if (regKeys.length && ['stars', 'all'].includes(type)) {
            starResponse = await createGreetCard(regKeys, this.selectedStore.id, zip, merge)
          }
          if (messages.length && ['meteorites', 'all'].includes(type)) {
            shootingStarResponse = await createGreetCard(
              null,
              this.selectedStore.id,
              zip,
              merge,
              messages,
              `${this.selectedStore.id}_greeting_cards_${moment().format(
                'D.M.YYYY'
              )}_meteorites.pdf`
            )
          }

          const previewGreetingCards = (data) => {
            if (!data) {
              return
            }

            if (zip) {
              let blob = new Blob([data], { type: 'application/zip' })
              const url = window.URL.createObjectURL(blob)
              window.open(url, '_blank')
            } else {
              data.forEach((item) => {
                if (typeof item === 'string') {
                  window.open(this.apiUrl + item, '_blank')
                } else {
                  window.open(item.url, '_blank')
                }
              })
            }
          }

          previewGreetingCards(starResponse?.data)
          previewGreetingCards(shootingStarResponse?.data)
        } catch (error) {
          console.log(error)
        } finally {
          this.loading = false
        }
      },
      async generateStarChart({ stars: regKeys }) {
        try {
          this.loading = true
          const { data } = await createStarInfoCard(regKeys, this.selectedStore.id)

          if (data.length === 0) {
            alert('No greeting message found!')
          }

          // stars
          data.forEach((item) => {
            if (typeof item === 'string') {
              window.open(this.apiUrl + item, '_blank')
            } else {
              window.open(item.url, '_blank')
            }
          })
        } catch (error) {
          console.log(error)
        } finally {
          this.loading = false
        }
      },
      async generateCardStarChart({ stars: regKeys }) {
        try {
          this.loading = true
          const { data } = await createStarInfoCard(regKeys, this.selectedStore.id, {
            template: 'card',
          })

          if (data.length === 0) {
            alert('No greeting message found!')
          }

          // stars
          data.forEach((item) => {
            if (typeof item === 'string') {
              window.open(this.apiUrl + item, '_blank')
            } else {
              window.open(item.url, '_blank')
            }
          })
        } catch (error) {
          console.log(error)
        } finally {
          this.loading = false
        }
      },
      async generateZodiacNecklace({ stars: regKeys, lineItems }) {
        try {
          this.loading = true
          const props = lineItems.filter((li) => li.parsedItem).map((li) => li.parsedItem._props)
          // console.log(list)
          const { data } = await createZodiacNecklace(regKeys, this.selectedStore.id, props)

          if (data.length === 0) {
            alert('No greeting message found!')
          }

          console.log('data', data)

          // stars
          data.forEach((item) => {
            if (typeof item === 'string') {
              window.open(this.apiUrl + item, '_blank')
            } else {
              window.open(item.url, '_blank')
            }
          })
        } catch (error) {
          console.log(error)
        } finally {
          this.loading = false
        }
      },
      async starChartZip(zip) {
        try {
          this.loading = true
          const regKeys = []
          this.selected.forEach((selection) => {
            regKeys.push(...selection.stars)
          })

          const responseType = zip ? 'zip' : 'merged_pdf'
          const { data } = await createStarInfoCard(regKeys, this.selectedStore.id, {
            responseType,
          })

          if (zip) {
            const filename = `STAR_CHART_${this.selectedStore.id}_${moment().format(
              'DD.MM.YYYY'
            )}.zip`
            const urls = data?.map(({ url }) => url) || []
            this.createAndDownloadZip(urls, filename)
          } else {
            data.forEach((item) => {
              if (typeof item === 'string') {
                window.open(this.apiUrl + item, '_blank')
              } else {
                window.open(item.url, '_blank')
              }
            })
          }
        } catch (error) {
          console.log(error)
        } finally {
          this.loading = false
        }
      },
      async generateHoroscopeAssets(orderId, orderNumber, version) {
        try {
          this.loading = true
          const response = await createHoroscopeAssets(orderId, version, this.selectedStore.id)

          // Response is one structured bundle per horoscope product:
          // { files: [...], combined: {url} | null, archive: {url} | null }.
          const bundles = response.data || []

          for (const bundle of bundles) {
            if (version === 'print') {
              // Download the server-built zip directly (no client-side re-zipping).
              if (bundle.archive?.url) {
                await this.downloadUrl(bundle.archive.url)
              } else if (bundle.files?.length) {
                // Fallback: zip the individual files client-side.
                await this.createAndDownloadZip(
                  bundle.files.map((f) => f.url),
                  `${orderNumber}_${version}.zip`
                )
              }
            } else {
              // Digital: open the combined PDF (cover + wheel) in a new tab.
              if (bundle.combined?.url) {
                window.open(bundle.combined.url, '_blank')
              } else {
                // Fallback: open the individual files.
                const files = bundle.files || []
                files.forEach((f) => f.url && window.open(f.url, '_blank'))
              }
            }
          }
        } catch (error) {
          console.log(error)
        } finally {
          this.loading = false
        }
      },
      async downloadUrl(url) {
        if (!url) return

        const response = await fetch(url)
        const blob = await response.blob()
        const filename = decodeURIComponent(url.substring(url.lastIndexOf('/') + 1))
        await this.downloadBlob(blob, filename)
      },
      async createAndDownloadZip(urls, filename) {
        const zip = new JSZip()
        const promises = urls.map(async (url) => {
          const response = await fetch(url)
          const blob = await response.blob()
          const filename = decodeURIComponent(url.substring(url.lastIndexOf('/') + 1))
          zip.file(filename, blob)
        })

        await Promise.all(promises)
        const zipBlob = await zip.generateAsync({ type: 'blob' })
        await this.downloadBlob(zipBlob, filename)
      },
      async downloadBlob(blob, filename) {
        const blobUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = filename
        link.click()
        URL.revokeObjectURL(blobUrl)
      },
      async generateCert({ id }, color, version) {
        try {
          this.loading = true
          const { data } = await createCert({
            orderIds: [id],
            color: color.id,
            store: this.selectedStore.id,
            version,
          })

          data.forEach((item) => {
            if (typeof item === 'string') {
              window.open(this.apiUrl + item, '_blank')
            } else {
              window.open(item.url, '_blank')
            }
          })
        } catch (error) {
          console.log(error)
        } finally {
          this.loading = false
        }
      },
      async renewRegistry(item) {
        try {
          this.loading = true
          const renew = confirm(
            'Are you sure you want to re register the star? Current star will be removed and a new random star will be registered. This action is irreversible...'
          )
          if (!renew) return
          await renewRegistryShopify(item.id, this.selectedStore.id)
        } catch (error) {
          console.log(error)
        } finally {
          this.loading = false
        }
      },
      async downloadZip(version, combined) {
        try {
          this.loading = true
          const starOrderIds = this.selected.filter((i) => i.variants.length).map((i) => i.id)
          const shootingStarOrderIds = this.selected
            .filter((i) => this.isShootingStarOrder(i))
            .map((i) => i.id)
          const responseType = combined ? 'merged_pdf' : 'zip'
          let response, starData, shootingStarData

          if (starOrderIds.length) {
            response = await createCert({
              orderIds: starOrderIds,
              store: this.selectedStore.id,
              version,
              responseType,
            })
            starData = response.data
          }

          if (shootingStarOrderIds.length) {
            response = await createShootingStarCert({
              orderIds: shootingStarOrderIds,
              store: this.selectedStore.id,
              version,
              responseType,
            })
            shootingStarData = response.data
          }

          const urls = [
            ...(starData?.map(({ url }) => url) || []),
            ...(shootingStarData?.map(({ url }) => url) || []),
          ]

          const filename = `${this.selectedStore.id}_${moment().format('DD.MM.YYYY')}.zip`
          this.createAndDownloadZip(urls, filename)

          /* data.forEach((item) => {
          if (typeof item === 'string') {
            window.open(this.apiUrl + item, '_blank')
          } else {
            window.open(item.url, '_blank')
          }
        }) */
        } catch (error) {
          console.log(error)
        } finally {
          this.loading = false
        }
      },
      async changeStore(e) {
        try {
          this.selected = [] // Reset selection when changing stores
          this.$router
            .replace({ params: { id: e?.id }, query: { ...this.$route.query } })
            .catch(() => {})
        } catch (error) {
          console.log(error)
        }
      },
      getRegNum(stars) {
        return stars[0]
      },
      getBadgeColor(color, index) {
        try {
          color = color[0].toLowerCase()
          if (!color.includes(' & ')) index = 0
          if (!color) return 'secondary'
          return (
            this.selectedStore.colors.find((c) =>
              [c.value, c.id].includes(color.split(' & ')[index])
            ).color || 'secondary'
          )
        } catch (error) {
          return 'secondary'
        }
      },
      getImageSrc(color, item) {
        return utils.getImageSrc(color, this.selectedStore, item)
      },
      getFulfillStatus(status) {
        const successIcon = 'mdi-checkbox-marked-circle-outline'
        const failureIcon = 'mdi-checkbox-blank-circle-outline'
        return status === 'fulfilled'
          ? ['Fulfilled', successIcon, 'green darken-4']
          : ['Unfulfilled', failureIcon, 'amber darken-4']
      },
      getFinanceStatus(status) {
        const statuses = {
          paid: {
            icon: 'mdi-checkbox-marked-circle-outline',
            color: 'blue-grey darken-1',
          },
          partially_paid: {
            icon: 'mdi-progress-check',
            color: 'amber darken-3',
          },
          pending: {
            icon: 'mdi-progress-clock',
            color: 'amber darken-3',
          },
          refunded: {
            icon: 'mdi-close-circle-outline',
            color: 'red darken-2',
          },
          partially_refunded: {
            icon: 'mdi-progress-close',
            color: 'amber darken-3',
          },
        }
        return {
          ...(statuses[status.toLowerCase()] || { icon: '' }),
          label: status.replace('_', ' '),
        }
      },
      previewCert(item) {
        this.generateCert(item, { value: 'default' }, 'digital')
      },
      async generateShootingStarCert(orderId, version, responseType) {
        try {
          this.loading = true
          const { data } = await createShootingStarCert({
            orderIds: Array.isArray(orderId) ? orderId : [orderId],
            store: this.selectedStore.id,
            version,
            responseType,
          })

          data.forEach((item) => {
            if (typeof item === 'string') {
              window.open(this.apiUrl + item, '_blank')
            } else {
              window.open(item.url, '_blank')
            }
          })
        } catch (error) {
          console.log(error)
        } finally {
          this.loading = false
        }
      },
      hasStarItems(order) {
        return (order.variants?.length ?? 0) > 0
      },
      // One chip per product kind in the order; mixed orders stack them
      // (same trick as the two-colour DUONOVA chip).
      orderBadges(order) {
        const badges = []

        if (this.isHoroscopeProduct(order)) {
          badges.push({ content: 'HSK', color: this.getBadgeColor(order.colors, 0) })
        }

        if (this.hasStarItems(order)) {
          const variant = order.variants[0]
          badges.push({ content: variant.slice(0, 3), color: this.getBadgeColor(order.colors, 0) })
          if (variant.includes('DUONOVA')) {
            badges.push({
              content: variant.slice(0, 3),
              color: this.getBadgeColor(order.colors, 1),
            })
          }
        }

        if (this.isShootingStarOrder(order)) badges.push({ content: 'SSP', color: 'green' })

        return badges
      },
      // The nested colour list lives in a detached menu, so the outer menu has
      // close-on-content-click off and is closed by hand after picking.
      runCertAction(action) {
        this.openCertMenu = null
        action()
      },
      isMixedCertOrder(order) {
        return !!this.isShootingStarOrder(order) && this.hasStarItems(order)
      },
      isShootingStarOrder(order) {
        return order.shootingStars?.length
      },
    },
    filters: {
      moment: function (date) {
        return moment(date).calendar(null, {
          lastWeek: 'dddd [at] hh:mm a',
          lastDay: '[Yesterday at] hh:mm a',
          sameDay: '[Today at] hh:mm a',
          nextDay: '[Tomorrow at] hh:mm a',
          nextWeek: '[Next] dddd',
          sameElse: 'MMM D [at] hh:mm a',
        })
      },
    },
    computed: {
      activeHeaders() {
        return this.headers
          .filter((header) => header.active)
          .map((h) => ({ ...h, text: this.$t(h.text) }))
      },
    },
    watch: {
      $route: async function (to, from) {
        // searchOrders() already refetched; a pure ?search= sync must not fetch twice
        if (to.params.id === from?.params.id && to.query.search !== from?.query.search) return

        try {
          this.loading = true
          this.selected = []
          await this.fetchOrders()
        } catch (error) {
          console.log(error)
        } finally {
          this.loading = false
        }
      },
      activeHeaders: function () {
        localStorage.setItem('order-headers', JSON.stringify(this.headers))
      },

      groupedAs: function () {
        if (this.groupedAs === null) return localStorage.removeItem('order-group')
        localStorage.setItem('order-group', this.groupedAs)
      },
    },
    mounted: async function () {
      this.isAdmin = await isUserAdmin()
      this.stores = await getStoresProtected()
      const headers = localStorage.getItem('order-headers')
      if (headers) this.headers = JSON.parse(headers)
      this.groupedAs = localStorage.getItem('order-group') || null
      this.searchQuery = this.$route.query.search || ''

      try {
        await this.fetchOrders()
      } catch (error) {
        console.log(error)
      }

      console.log(this.headers)
      this.loading = false
    },
    components: { customerCard, emailDialog, horoscopeBookDialog },
  }
</script>

<style scoped>
  /* Vuetify indents list-group children; the cert picker is flat, so drop it */
  .cert-menu-list ::v-deep .v-list-group__items .v-list-item {
    padding-left: 16px;
  }

  .fixed-col {
    position: sticky;
    left: 0;
    background-color: #1c1d1e;
    transition: background-color 0.2s;
    /* Add a smooth transition effect */
  }

  /* Change background color on column hover */
  .fixed-col:hover,
  .v-data-table tbody tr:hover .fixed-col {
    background-color: #5c5c5d;
    /* Change to the desired hover color */
  }

  /* Change background color on row hover */
  .v-data-table tbody tr:hover {
    background-color: #5c5c5d;
    /* Change to the desired hover color */
  }
</style>
